import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { FiInfo } from 'react-icons/fi';

/**
 * Componente que renderiza Markdown de forma limpia sin duplicar íconos
 */
export default function MarkdownRenderer({ content }) {
  const components = {
    h2({ children }) {
      return (
        <h2 className="section-title" style={{ display: 'flex', alignItems: 'center', gap: '8px', fontFamily: "'Gabarito', sans-serif", fontSize: '20px', fontWeight: 600, marginTop: '2rem', marginBottom: '0.85rem', color: '#0f172a' }}>
          {children}
        </h2>
      );
    },
    h3({ children }) {
      return <h3 style={{ fontFamily: "'Gabarito', sans-serif", fontSize: '16px', fontWeight: 600, marginTop: '1.25rem', marginBottom: '0.6rem', color: '#1e293b' }}>{children}</h3>;
    },
    h4({ children }) {
      return <h4 style={{ fontFamily: "'Gabarito', sans-serif", fontSize: '14.5px', fontWeight: 600, marginTop: '1rem', marginBottom: '0.4rem', color: '#334155' }}>{children}</h4>;
    },
    p({ children }) {
      return <p style={{ fontWeight: 300, fontSize: '13.5px', lineHeight: 1.6, color: '#475569', marginBottom: '0.85rem' }}>{children}</p>;
    },
    ul({ children }) {
      return <ul style={{ paddingLeft: '20px', marginBottom: '0.85rem' }}>{children}</ul>;
    },
    ol({ children }) {
      return <ol style={{ paddingLeft: '20px', marginBottom: '0.85rem' }}>{children}</ol>;
    },
    li({ children }) {
      return <li style={{ fontWeight: 300, fontSize: '13.5px', lineHeight: 1.6, color: '#475569', marginBottom: '0.35rem' }}>{children}</li>;
    },
    strong({ children }) {
      return <strong style={{ fontWeight: 600, fontSize: '13.5px', color: '#0f172a' }}>{children}</strong>;
    },
    b({ children }) {
      return <b style={{ fontWeight: 600, fontSize: '13.5px', color: '#0f172a' }}>{children}</b>;
    },
    blockquote({ children }) {
      return (
        <div className="info-box" style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', background: '#f0fdf4', borderLeft: '3px solid #00bf63', padding: '12px 14px', borderRadius: '6px', margin: '14px 0' }}>
          <FiInfo size={16} style={{ color: '#00bf63', flexShrink: 0, marginTop: '2px' }} />
          <div style={{ fontWeight: 300, fontSize: '13px', color: '#166534' }}>{children}</div>
        </div>
      );
    },
    code({ node, inline, className, children, ...props }) {
      const codeString = Array.isArray(children) ? children.join('') : String(children || '');
      const isMultiLine = codeString.includes('\n');
      const hasLang = Boolean(className && className.startsWith('language-'));
      const isInline = inline || (!isMultiLine && !hasLang);

      if (isInline) {
        return (
          <code
            style={{
              background: '#f1f5f9',
              color: '#0f172a',
              border: '1px solid #cbd5e1',
              padding: '2px 6px',
              borderRadius: '5px',
              fontSize: '0.86em',
              fontFamily: "'Fira Code', monospace",
              fontWeight: 500,
              display: 'inline-block',
              margin: '0 2px',
              wordBreak: 'break-word'
            }}
            {...props}
          >
            {children}
          </code>
        );
      }

      return (
        <div style={{ position: 'relative', margin: '0.85rem 0' }}>
          <pre style={{ background: '#0f172a', color: '#f8fafc', padding: '14px', borderRadius: '8px', overflowX: 'auto', fontSize: '12.5px', fontFamily: "'Fira Code', monospace", fontWeight: 300, lineHeight: 1.5, border: '1px solid #1e293b' }}>
            <code className={className} {...props}>
              {children}
            </code>
          </pre>
        </div>
      );
    },
    table({ children }) {
      return (
        <div style={{ overflowX: 'auto', margin: '0.85rem 0' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
            {children}
          </table>
        </div>
      );
    },
    th({ children }) {
      return <th style={{ background: '#f8fafc', padding: '8px 10px', borderBottom: '2px solid #e2e8f0', fontWeight: 600, fontSize: '13px', color: '#0f172a' }}>{children}</th>;
    },
    td({ children }) {
      return <td style={{ padding: '8px 10px', borderBottom: '1px solid #e2e8f0', fontWeight: 300, fontSize: '13px', color: '#475569' }}>{children}</td>;
    }
  };

  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
      {content}
    </ReactMarkdown>
  );
}
